/**
 * scopedStyles.js — the full ClientWave360 landing-page design system.
 *
 * REQUEST-74 redesign: centered gradient hero, icon card grids, and the
 * Let's Connect / Find Me split, matching the provided mockups. Every selector
 * is scoped under `[data-cf360]` so none of these rules leak into the
 * surrounding CRM shell (Bulma + app.scss). Brand color flows through the
 * OKLCH custom properties set by `buildPalette()` on the scope element; static
 * design tokens are declared here so they survive the container's
 * `all: initial` reset (custom properties are exempt from `all`).
 *
 * Inter for all text, inline SVG icons only.
 */

export const CF360_SCOPED_CSS = `
[data-cf360] {
  /* Palette defaults (overwritten by the computed brand palette set inline) */
  --brand-ink: rgb(28, 32, 38);
  --brand-ink-deep: rgb(20, 24, 29);
  --brand-ink-soft: rgb(48, 54, 62);
  --brand-accent: rgb(43, 164, 83);
  --brand-accent-hover: rgb(31, 132, 65);
  --brand-secondary: rgb(43, 164, 83);
  --brand-accent-bright: rgb(74, 190, 112);
  --brand-wash: rgb(247, 248, 247);
  --brand-tint: rgb(235, 239, 236);
  --brand-line: rgb(222, 227, 232);
  --brand-tertiary: #f1f5f9;
  --grad-start: rgb(34, 48, 120);
  --grad-mid: rgb(24, 30, 62);
  --grad-end: #141414;
  --text-strong: rgb(23, 28, 33);
  --text-body: rgb(62, 70, 66);
  --text-muted: rgb(108, 116, 112);
  --on-ink: #fff;
  --on-ink-muted: rgb(205, 212, 224);
  --on-ink-faint: rgb(150, 158, 175);
  --on-ink-line: rgb(255 255 255 / 0.22);

  --font-text: "Inter", -apple-system, "Segoe UI", "Helvetica Neue", sans-serif;

  --container: 1120px;
  --radius-sm: 8px;
  --radius: 12px;
  --radius-lg: 16px;
  --shadow-card: 0 1px 2px rgb(15 20 17 / 0.05), 0 14px 34px -14px rgb(15 20 17 / 0.12);
  --ease-out: cubic-bezier(0.22, 1, 0.36, 1);
}

/* ---------- Reset & base ---------- */

[data-cf360] *, [data-cf360] *::before, [data-cf360] *::after { box-sizing: border-box; }

/* The CRM globally styles bare elements (e.g. base.scss sets p font-family to var(--font-family))
   and applies an inherited letter-spacing. Re-assert the page's typography baseline on every
   descendant; per-element class rules below (higher specificity) still win. */
[data-cf360] * {
  font-family: var(--font-text);
  letter-spacing: normal;
  text-transform: none;
}

[data-cf360] [hidden] { display: none !important; }

[data-cf360] section[id] { scroll-margin-top: 76px; }

[data-cf360] img { max-width: 100%; display: block; }

[data-cf360] h1, [data-cf360] h2, [data-cf360] h3 { margin: 0; color: var(--text-strong); }

[data-cf360] a { color: var(--brand-accent); text-decoration: none; }
[data-cf360] a:hover { color: var(--brand-accent-hover); }

[data-cf360] :focus-visible {
  outline: 2px solid var(--brand-accent);
  outline-offset: 3px;
  border-radius: 2px;
}

[data-cf360] .cw-container {
  max-width: var(--container);
  margin-inline: auto;
  padding-inline: clamp(20px, 4vw, 40px);
}

/* ---------- Buttons ---------- */

[data-cf360] .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font: 600 15px/1 var(--font-text);
  padding: 12px 22px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s var(--ease-out), color 0.2s var(--ease-out),
    border-color 0.2s var(--ease-out), transform 0.2s var(--ease-out);
}
[data-cf360] .btn:active { transform: scale(0.98); }

[data-cf360] .btn-primary { background: var(--brand-accent); color: #fff; }
[data-cf360] .btn-primary:hover { background: var(--brand-accent-hover); color: #fff; }

/* Ghost — white outline on dark surfaces (hero/header), accent outline on light. */
[data-cf360] .btn-ghost {
  background: transparent;
  color: var(--on-ink);
  border-color: rgb(255 255 255 / 0.55);
}
[data-cf360] .btn-ghost:hover { border-color: #fff; color: #fff; background: rgb(255 255 255 / 0.08); }

[data-cf360] .btn-lg { padding: 14px 28px; font-size: 15px; }
[data-cf360] .btn-block { width: 100%; }

/* ---------- Header ---------- */

[data-cf360] .site-header {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 50;
  transition: background-color 0.3s var(--ease-out), box-shadow 0.3s var(--ease-out);
}

[data-cf360] .header-inner {
  display: flex;
  align-items: center;
  gap: 28px;
  height: 68px;
}

[data-cf360] .header-brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
  min-width: 0;
}
[data-cf360] .header-logo { height: 30px; width: auto; }
[data-cf360] .header-wordmark {
  font: 700 16px/1 var(--font-text);
  letter-spacing: -0.01em;
  color: var(--on-ink);
  transition: color 0.3s var(--ease-out);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-cf360] .header-nav { display: flex; gap: 24px; }
[data-cf360] .header-nav a {
  font: 500 14px/1 var(--font-text);
  color: var(--on-ink-muted);
  transition: color 0.2s var(--ease-out);
}
[data-cf360] .header-nav a:hover { color: var(--on-ink); }

[data-cf360] .header-actions { display: flex; align-items: center; gap: 10px; }
[data-cf360] .btn-header { padding: 10px 16px; font-size: 13.5px; }

[data-cf360] .site-header:not(.scrolled) .header-logo {
  background: #fff;
  border-radius: 7px;
  padding: 3px;
}

[data-cf360] .site-header.scrolled {
  background: rgb(255 255 255 / 0.88);
  -webkit-backdrop-filter: blur(14px);
  backdrop-filter: blur(14px);
  box-shadow: inset 0 -1px 0 var(--brand-line);
}
[data-cf360] .site-header.scrolled .header-wordmark { color: var(--text-strong); }
[data-cf360] .site-header.scrolled .header-nav a { color: var(--text-muted); }
[data-cf360] .site-header.scrolled .header-nav a:hover { color: var(--brand-accent); }
[data-cf360] .site-header.scrolled .btn-ghost {
  color: var(--brand-accent);
  border-color: var(--brand-line);
  background: transparent;
}
[data-cf360] .site-header.scrolled .btn-ghost:hover {
  color: var(--brand-accent-hover);
  border-color: var(--brand-accent);
}

/* ---------- Hero ---------- */

[data-cf360] .cw-hero {
  position: relative;
  background: linear-gradient(180deg, var(--grad-start) 0%, var(--grad-mid) 62%, var(--grad-end) 100%);
  color: var(--on-ink);
  overflow: hidden;
  text-align: center;
}

[data-cf360] .hero-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: clamp(110px, 15vh, 150px);
  padding-bottom: clamp(60px, 9vh, 96px);
}

[data-cf360] .hero-portrait {
  margin: 0 0 22px;
  width: 108px;
  height: 108px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 0 0 3px rgb(255 255 255 / 0.85), 0 18px 40px -18px rgb(0 0 0 / 0.6);
  background: rgb(255 255 255 / 0.12);
}
[data-cf360] .hero-portrait img { width: 100%; height: 100%; object-fit: cover; }
[data-cf360] .hero-monogram {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  font: 700 38px/1 var(--font-text);
  color: var(--on-ink);
}

[data-cf360] .hero-name {
  font: 800 clamp(36px, 5.4vw, 56px)/1.08 var(--font-text);
  letter-spacing: -0.02em;
  color: var(--on-ink);
  margin: 0 0 14px;
  text-wrap: balance;
}

[data-cf360] .hero-title {
  margin: 0 0 20px;
  font: 600 15px/1.5 var(--font-text);
  color: var(--brand-accent-bright);
}
[data-cf360] .hero-title em { font-style: italic; font-weight: 500; }

[data-cf360] .hero-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 10px;
  font: 500 14.5px/1.5 var(--font-text);
  color: var(--on-ink-muted);
}
[data-cf360] .hero-meta svg { color: var(--on-ink-faint); flex: none; }
[data-cf360] .hero-meta a { color: var(--on-ink-muted); }
[data-cf360] .hero-meta a:hover { color: var(--on-ink); }

[data-cf360] .hero-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 26px;
}

/* ---------- Sections ---------- */

[data-cf360] .cw-section { padding-block: clamp(64px, 9vh, 104px); }

/* .section-head is a semantic header element; the CRM globally sets header to
   position:fixed (navbar.scss), which would pull it out of flow. Reset it here. */
[data-cf360] .section-head {
  position: static;
  width: auto;
  z-index: auto;
  max-width: 720px;
  margin: 0 auto clamp(32px, 5vh, 48px);
  text-align: center;
}

[data-cf360] .section-eyebrow {
  margin: 0 0 12px;
  font: 700 12.5px/1 var(--font-text);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--brand-accent);
}

[data-cf360] .section-title {
  font: 800 clamp(28px, 3.2vw, 38px)/1.15 var(--font-text);
  letter-spacing: -0.015em;
  text-wrap: balance;
}

[data-cf360] .section-bar {
  display: block;
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: var(--brand-accent);
  margin: 18px auto 0;
}

/* ---------- About ---------- */

[data-cf360] .about-body {
  max-width: 680px;
  margin-inline: auto;
}
[data-cf360] .about-body p { margin: 0 0 20px; font-size: 16px; line-height: 1.8; color: var(--text-body); }
[data-cf360] .about-body p:last-child { margin-bottom: 0; }

/* ---------- Products & Services ---------- */

[data-cf360] .section-products { background: var(--brand-tertiary); }

[data-cf360] .product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

[data-cf360] .product-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 22px 12px 18px;
  background: #fff;
  border: 1px solid var(--brand-line);
  border-radius: var(--radius);
  text-align: center;
  transition: border-color 0.25s var(--ease-out), transform 0.25s var(--ease-out),
    box-shadow 0.25s var(--ease-out);
}
[data-cf360] .product-card:hover {
  border-color: var(--brand-accent);
  transform: translateY(-2px);
  box-shadow: var(--shadow-card);
}

[data-cf360] .product-icon {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--brand-accent) 18%, #fff);
  color: var(--brand-accent);
}

[data-cf360] .product-name {
  font: 700 13px/1.35 var(--font-text);
  color: var(--text-strong);
}

/* ---------- Carriers ---------- */

[data-cf360] .carriers-intro {
  max-width: 520px;
  margin: -14px auto clamp(28px, 4vh, 40px);
  text-align: center;
  color: var(--text-body);
}

[data-cf360] .carriers-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, max-content));
  gap: 14px 110px;
  justify-content: center;
  margin: 0;
  padding: 0;
  list-style: none;
}
[data-cf360] .carriers-list li {
  position: relative;
  padding-left: 18px;
  font: 700 15.5px/1.5 var(--font-text);
  color: var(--text-strong);
}
[data-cf360] .carriers-list li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.62em;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--brand-accent);
}

/* ---------- Find an Agent ---------- */

[data-cf360] .section-agents { background: var(--brand-tertiary); }

[data-cf360] .agent-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 18px;
  margin: 0;
  padding: 0;
  list-style: none;
}

[data-cf360] .agent-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 28px 20px 24px;
  background: #fff;
  border: 1px solid var(--brand-line);
  border-radius: var(--radius);
  text-align: center;
  transition: border-color 0.25s var(--ease-out), transform 0.25s var(--ease-out),
    box-shadow 0.25s var(--ease-out);
}
[data-cf360] .agent-card:hover {
  border-color: var(--brand-accent);
  transform: translateY(-2px);
  box-shadow: var(--shadow-card);
}

[data-cf360] .agent-photo {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 8px;
  background: color-mix(in srgb, var(--brand-accent) 18%, #fff);
}
[data-cf360] .agent-photo img { width: 100%; height: 100%; object-fit: cover; }
[data-cf360] .agent-monogram {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  font: 700 26px/1 var(--font-text);
  color: var(--brand-accent);
}

[data-cf360] .agent-name { font: 700 17px/1.3 var(--font-text); }
[data-cf360] .agent-title { margin: 0; font: 500 13.5px/1.4 var(--font-text); color: var(--brand-accent); }
[data-cf360] .agent-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 2px 0 0;
  font: 500 13.5px/1.4 var(--font-text);
  color: var(--text-muted);
}
[data-cf360] .agent-meta svg { color: var(--brand-accent); flex: none; }
[data-cf360] .agent-meta a { color: var(--text-muted); }
[data-cf360] .agent-meta a:hover { color: var(--brand-accent); }
[data-cf360] .agent-link {
  margin-top: 14px;
  padding: 9px 20px;
  font-size: 13.5px;
  color: var(--brand-accent);
  border-color: var(--brand-line);
}
[data-cf360] .agent-link:hover {
  color: var(--brand-accent-hover);
  border-color: var(--brand-accent);
  background: transparent;
}

/* ---------- Let's Connect ---------- */

[data-cf360] .section-connect { background: var(--brand-tertiary); }

[data-cf360] .connect-inner {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: clamp(32px, 5vw, 64px);
  align-items: start;
}

[data-cf360] .connect-copy .section-eyebrow { text-align: left; }
[data-cf360] .connect-copy .section-title { text-align: left; font-size: clamp(26px, 2.8vw, 34px); }

[data-cf360] .connect-sub {
  margin: 16px 0 24px;
  color: var(--text-body);
  max-width: 44em;
}

[data-cf360] .connect-benefits {
  display: grid;
  gap: 12px;
  margin: 0 0 30px;
  padding: 0;
  list-style: none;
}
[data-cf360] .connect-benefits li {
  display: flex;
  align-items: center;
  gap: 11px;
  font: 500 15px/1.5 var(--font-text);
  color: var(--text-strong);
}
[data-cf360] .benefit-mark {
  display: grid;
  place-items: center;
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--brand-accent);
  color: #fff;
}

[data-cf360] .connect-booking h3 { font: 700 17px/1.3 var(--font-text); margin-bottom: 6px; }
[data-cf360] .connect-booking p { margin: 0 0 14px; font-size: 14.5px; color: var(--text-body); }

/* ---------- Contact form (card + modal) ---------- */

[data-cf360] .contact-form {
  background: #fff;
  border: 1px solid var(--brand-line);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  padding: clamp(24px, 3.5vw, 34px);
  display: grid;
  gap: 16px;
}

[data-cf360] .form-title { font: 700 19px/1.2 var(--font-text); margin-bottom: 2px; }

[data-cf360] .field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

[data-cf360] .cw-field { display: grid; gap: 6px; }
[data-cf360] .cw-field label {
  font: 600 13px/1 var(--font-text);
  color: var(--text-strong);
}
[data-cf360] .cw-field input,
[data-cf360] .cw-field textarea {
  font: 500 15px/1.4 var(--font-text);
  color: var(--text-strong);
  padding: 10px 12px;
  border: 1px solid var(--brand-line);
  border-radius: var(--radius-sm);
  background: #fff;
  width: 100%;
  transition: border-color 0.2s var(--ease-out), box-shadow 0.2s var(--ease-out);
}
[data-cf360] .cw-field textarea { resize: vertical; }

/* Override the CRM's global Bulma form-control rules (which use !important on bare
   input/textarea) so the field styling matches the mockup exactly. */
[data-cf360] .cw-field input,
[data-cf360] .cw-field textarea {
  font-size: 15px !important;
  line-height: 1.4 !important;
  color: var(--text-strong) !important;
  border-color: var(--brand-line) !important;
  background-color: #fff !important;
  box-shadow: none !important;
}
[data-cf360] .cw-field input:hover,
[data-cf360] .cw-field textarea:hover { border-color: var(--brand-line) !important; }
[data-cf360] .cw-field input:focus,
[data-cf360] .cw-field textarea:focus {
  outline: none;
  border-color: var(--brand-accent) !important;
  background-color: #fff !important;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-accent) 16%, transparent) !important;
}
[data-cf360] .cw-field input.invalid { border-color: rgb(186, 54, 54) !important; }

[data-cf360] .field-error {
  margin: 0;
  font: 500 12.5px/1.4 var(--font-text);
  color: rgb(186, 54, 54);
}

[data-cf360] .consent {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13.5px;
  color: var(--text-body);
  cursor: pointer;
}
[data-cf360] .consent input {
  margin-top: 3px;
  width: 15px; height: 15px;
  accent-color: var(--brand-accent);
}

[data-cf360] .form-note { margin: 0; font-size: 11.5px; line-height: 1.55; color: var(--text-muted); }

[data-cf360] .form-success {
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 12px;
  padding: 24px 0;
}
[data-cf360] .form-success .success-mark {
  display: grid;
  place-items: center;
  width: 44px; height: 44px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--brand-accent) 18%, #fff);
  color: var(--brand-accent);
}
[data-cf360] .form-success h3 { font: 700 22px/1.2 var(--font-text); }
[data-cf360] .form-success p { margin: 0; color: var(--text-muted); }

/* ---------- Get in Touch modal ---------- */

[data-cf360] .cw-modal {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgb(20 20 20 / 0.55);
  overflow-y: auto;
}
[data-cf360] .cw-modal-card {
  position: relative;
  width: min(480px, 100%);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  border-radius: var(--radius-lg);
  animation: cf360-rise 0.35s var(--ease-out) both;
}
[data-cf360] .cw-modal-card .contact-form { border: 0; }
[data-cf360] .cw-modal-close {
  position: absolute;
  top: 14px;
  right: 16px;
  z-index: 1;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--text-muted);
  font: 400 24px/1 var(--font-text);
  cursor: pointer;
}
[data-cf360] .cw-modal-close:hover { color: var(--text-strong); background: var(--brand-tertiary); }

/* ---------- Find Me / Get In Touch ---------- */

[data-cf360] .findme-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(28px, 4vw, 56px);
  align-items: start;
}

[data-cf360] .findme-label {
  font: 700 12.5px/1 var(--font-text);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 18px;
}

[data-cf360] .findme-list {
  display: grid;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}
[data-cf360] .findme-list > li { display: flex; gap: 12px; }
[data-cf360] .findme-icon {
  display: grid;
  place-items: center;
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--brand-accent) 18%, #fff);
  color: var(--brand-accent);
}
[data-cf360] .findme-term {
  display: block;
  font: 600 11px/1.4 var(--font-text);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 2px;
}
[data-cf360] .findme-value { display: block; font: 500 14.5px/1.5 var(--font-text); color: var(--text-strong); }
[data-cf360] a.findme-value:hover, [data-cf360] .findme-value a:hover { color: var(--brand-accent); }
[data-cf360] .findme-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 3px; }
[data-cf360] .findme-tag {
  padding: 3px 11px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--brand-accent) 16%, #fff);
  color: var(--brand-accent);
  font: 600 12.5px/1.5 var(--font-text);
}

[data-cf360] .hours-table { width: 100%; border-collapse: collapse; }
[data-cf360] .hours-table td {
  padding: 8px 0;
  font-size: 14.5px;
  border-bottom: 1px solid var(--brand-tertiary);
}
[data-cf360] .hours-table tr:last-child td { border-bottom: 0; }
[data-cf360] .hours-table .hours-days { color: var(--text-strong); font-weight: 600; }
[data-cf360] .hours-table .hours-time {
  text-align: right;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}
[data-cf360] .hours-table tr.today .hours-days { color: var(--brand-accent); }
[data-cf360] .hours-table tr.today .hours-time { color: var(--text-strong); }
[data-cf360] .hours-table .hours-closed { font-style: normal; }

[data-cf360] .map-frame {
  position: relative;
  aspect-ratio: 16 / 11;
  border: 1px solid var(--brand-line);
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--brand-tertiary);
}
[data-cf360] .map-frame iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  filter: saturate(0.55) contrast(1.02);
}
[data-cf360] .map-directions {
  display: inline-block;
  margin-top: 12px;
  font: 600 13.5px/1 var(--font-text);
}

/* Condensed variant — centered phone/email tiles only. */
[data-cf360] .findme-tiles {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(36px, 6vw, 80px);
  margin: 0;
  padding: 0;
  list-style: none;
}
[data-cf360] .findme-tiles li {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
}
[data-cf360] .findme-tile-icon {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--brand-accent) 18%, #fff);
  color: var(--brand-accent);
  margin-bottom: 4px;
}
[data-cf360] .findme-tile-label {
  font: 600 11px/1.4 var(--font-text);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
}
[data-cf360] .findme-tiles a { font: 500 14px/1.5 var(--font-text); }

/* ---------- Footer ---------- */

[data-cf360] .site-footer { background: #fff; }

[data-cf360] .footer-social-band {
  display: flex;
  justify-content: center;
  padding-block: 8px 40px;
}
[data-cf360] .footer-social {
  list-style: none;
  display: flex;
  gap: 12px;
  margin: 0;
  padding: 0;
}
[data-cf360] .footer-social a {
  display: grid;
  place-items: center;
  width: 40px; height: 40px;
  border-radius: 50%;
  background: var(--brand-tertiary);
  color: var(--text-muted);
  transition: background-color 0.2s var(--ease-out), color 0.2s var(--ease-out);
}
[data-cf360] .footer-social a:hover { background: var(--brand-accent); color: #fff; }

[data-cf360] .footer-disclaimer-band {
  background: var(--brand-tertiary);
  border-top: 1px solid var(--brand-line);
}
[data-cf360] .footer-disclaimer {
  margin: 0;
  padding-block: 18px;
  font-size: 11.5px;
  line-height: 1.6;
  color: var(--text-muted);
}

[data-cf360] .footer-legal-band {
  background: linear-gradient(90deg, var(--grad-start) 0%, var(--grad-end) 100%);
  color: var(--on-ink-muted);
}
[data-cf360] .footer-legal {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 24px;
  padding-block: 20px;
}
[data-cf360] .footer-copyright { margin: 0; font-size: 13px; color: var(--on-ink-muted); }
[data-cf360] .footer-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 22px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 13px;
}
[data-cf360] .footer-links a { color: var(--on-ink-muted); }
[data-cf360] .footer-links a:hover { color: var(--on-ink); }
[data-cf360] .footer-powered { color: var(--on-ink-faint); }
[data-cf360] .footer-powered a { font-weight: 600; }

/* ---------- Motion ---------- */

@keyframes cf360-rise {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: none; }
}

[data-cf360] .reveal-load { animation: cf360-rise 0.8s var(--ease-out) both; }
[data-cf360] .hero-inner .reveal-load:nth-child(1) { animation-delay: 0.05s; }
[data-cf360] .hero-inner .reveal-load:nth-child(2) { animation-delay: 0.12s; }
[data-cf360] .hero-inner .reveal-load:nth-child(3) { animation-delay: 0.19s; }
[data-cf360] .hero-inner .reveal-load:nth-child(4) { animation-delay: 0.26s; }
[data-cf360] .hero-inner .reveal-load:nth-child(5) { animation-delay: 0.33s; }
[data-cf360] .hero-inner .reveal-load:nth-child(6) { animation-delay: 0.4s; }

[data-cf360] .reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.7s var(--ease-out), transform 0.7s var(--ease-out);
}
[data-cf360] .reveal.in { opacity: 1; transform: none; }

@media (prefers-reduced-motion: reduce) {
  [data-cf360] .reveal-load { animation: none; }
  [data-cf360] .reveal { opacity: 1; transform: none; transition: none; }
  [data-cf360] .site-header, [data-cf360] .btn, [data-cf360] .product-card, [data-cf360] .agent-card { transition: none; }
}

/* ---------- Responsive ---------- */

@media (max-width: 1023px) {
  [data-cf360] .connect-inner { grid-template-columns: 1fr; }
  [data-cf360] .findme-grid { grid-template-columns: 1fr 1fr; }
  [data-cf360] .findme-grid .findme-col:last-child { grid-column: 1 / -1; }
}

@media (max-width: 719px) {
  [data-cf360] .header-nav { display: none; }
  [data-cf360] .header-inner { height: 62px; gap: 12px; }
  [data-cf360] .btn-header { padding: 9px 13px; font-size: 13px; }
  [data-cf360] .hero-name { font-size: clamp(32px, 9vw, 40px); }
  [data-cf360] .field-row { grid-template-columns: 1fr; }
  [data-cf360] .findme-grid { grid-template-columns: 1fr; }
  [data-cf360] .carriers-list { grid-template-columns: minmax(0, max-content); }
  [data-cf360] .product-grid { grid-template-columns: repeat(auto-fill, minmax(112px, 1fr)); }
  [data-cf360] .footer-legal { flex-direction: column; align-items: center; text-align: center; }
}
`;
